import { $dt, definePreset, palette } from '@primeuix/themes';
import Material from '@primeuix/themes/material';

export const EboTheme = definePreset(Material, {
  //Your customizations, see the following sections for examples
  semantic: {
    primary: palette('#3f51b5'),
    surface: palette('gray'),
    colorScheme: {
      light: {
        primary: {
          color: '{primary.500}',
          contrastColor: '{surface.50}'
        },
        link: {
          color: '{indigo-500}'
        },
        surface: {
          color: '{surface.50}',
          b: {
            color: '{surface.100}'
          },
          c: {
            color: '{surface.200}'
          },
          d: {
            color: '{surface.300}'
          }
        },
        border: {
          color: '{surface.500}'
        },
        grid: {
          overflow: {
            color: '{surface.300}',
            contrastColor: '{text-color}'
          },
          group: {
            0: {
              color: '#008cff',
              contrastColor: '{text-color}'
            },
            1: {
              color: 'cyan',
              contrastColor: '{text-color}'
            },
            2: {
              color: 'lightskyblue',
              contrastColor: '{text-color}'
            },
            3: {
              color: 'lightsteelblue',
              contrastColor: '{text-color}'
            },
            4: {
              color: 'steelblue',
              contrastColor: '{text-color}'
            }
          }
        },
        highlight: {
          color: 'black',
          background: '{yellow.600}',
          border: {
            color: '{yellow.600}'
          }
        },
        flag: {
          color: '{text-color}',
          background: '{blue.600}',
          border: {
            color: '{blue.600}'
          }
        },
        warning: {
          color: '{text-color}',
          background: '{orange.700}',
          border: {
            color: '{orange.700}'
          }
        },
        danger: {
          color: 'white',
          background: '{red.600}',
          border: {
            color: '{red.600}'
          }
        },
        'grey-out': {
          color: 'white',
          background: '{neutral.300}'
        },
        darkened: {
          color: 'white',
          background: '{neutral.400}'
        }
      },
      dark: {
        primary: {
          color: '{primary.800}',
          contrastColor: '{surface.300}'
        },
        link: {
          color: '{blue-500}'
        },
        surface: {
          color: 'black',
          b: {
            color: '{surface.950}'
          },
          c: {
            color: '{surface.900}'
          },
          d: {
            color: '{surface.800}'
          }
        },
        border: {
          color: '{surface.500}'
        },
        grid: {
          overflow: {
            color: '{surface.900}',
            contrastColor: '{surface.100}'
          },
          group: {
            0: {
              color: '{slate.600}',
              contrastColor: '{text-color}'
            },
            1: {
              color: '{slate.700}',
              contrastColor: '{text-color}'
            },
            2: {
              color: '{slate.800}',
              contrastColor: '{text-color}'
            },
            3: {
              color: '{slate.900}',
              contrastColor: '{text-color}'
            },
            4: {
              color: '{slate.950}',
              contrastColor: '{text-color}'
            }
          }
        },
        highlight: {
          color: 'black',
          background: '{yellow.500}',
          border: {
            color: '{yellow.500}'
          }
        },
        flag: {
          color: '{text-color}',
          background: '{blue.500}',
          border: {
            color: '{blue.500}'
          }
        },
        warning: {
          color: '{text-color}',
          background: '{orange.900}',
          border: {
            color: '{orange.900}'
          }
        },
        danger: {
          color: '{text-color}',
          background: '{red.900}',
          border: {
            color: '{red.900}'
          }
        },
        'grey-out': {
          color: '{neutral.600}',
          background: '{neutral.800}'
        },
        darkened: {
          color: '{neutral.400}',
          background: '{neutral.700}'
        }
      }
    }
  },
  components: {
    autocomplete: {
      colorScheme: {
        light: {
          option: {
            selectedColor: 'inherit',
            selectedBackground: '{surface.200}'
          }
        },
        dark: {
          option: {
            selectedColor: 'white',
            selectedBackground: '{surface.900}'
          }
        }
      }
    },
    button: {
      colorScheme: {
        light: {
          root: {
            primary: {
              borderColor: '{primary.500}'
            },
            success: {
              color: 'white',
              background: '{green.500}'
            },
            info: {
              color: '{sky-950}',
              background: '{sky-200}'
            }
          },
          text: {
            primary: {
              color: '{primary.500}'
            }
          }
        },
        dark: {
          root: {
            primary: {
              borderColor: '{primary.400}'
            },
            success: {
              color: 'white',
              background: '{green.500}'
            },
            info: {
              color: '{sky-400}',
              background: '{sky-950}'
            }
          },
          text: {
            primary: {
              color: '{primary.400}'
            }
          },
          outlined: {
            primary: {
              color: '{primary.400}',
              borderColor: '{primary.400}'
            }
          }
        }
      }
    },
    card: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            color: 'currentColor'
          }
        },
        dark: {
          root: {
            background: '{surface.900}',
            color: 'currentColor',
            shadow:
              '0 2px 1px -1px rgba(255,255,255,.2), 0 1px 1px 0 rgba(255,255,255,.14), 0 1px 3px 0 rgba(255,255,255,.12)'
          }
        }
      },
      body: {
        padding: '0.5rem 1.5rem 1.5rem 1.5rem'
      }
    },
    dialog: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            color: 'black'
          }
        },
        dark: {
          root: {
            background: '{surface.900}',
            color: 'currentColor'
          }
        }
      }
    },
    divider: {
      colorScheme: {
        light: {
          content: {
            color: 'inherit',
            background: '{surface.0}'
          }
        },
        dark: {
          content: {
            color: 'white',
            background: '{surface.900}'
          }
        }
      }
    },
    fieldset: {
      colorScheme: {
        light: {
          root: {
            background: 'transparent',
            color: 'currentColor'
          },
          legend: {
            background: '{surface.0}'
          }
        },
        dark: {
          root: {
            background: 'transparent',
            color: 'currentColor'
          },
          legend: {
            background: '{surface.900}'
          }
        }
      },
      legend: {
        borderColor: '{surface.500}',
        color: 'currentColor'
      }
    },
    fileupload: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            color: 'currentColor'
          },
          header: {
            color: 'currentColor'
          }
        },
        dark: {
          root: {
            background: '{surface.900}',
            color: 'currentColor'
          },
          header: {
            color: 'currentColor'
          }
        }
      }
    },
    floatlabel: {
      colorScheme: {
        light: {
          root: {
            color: '{surface.500}',
            activeColor: '{surface.800}'
          }
        },
        dark: {
          root: {
            color: '{surface.500}',
            activeColor: '{surface.200}',
            focusColor: '{surface.200}'
          }
        }
      },
      on: {
        active: {
          background: '{surface.0}'
        }
      }
    },
    inputtext: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            color: 'black',
            disabledBackground: '{surface.200}',
            invalidBorderColor: $dt('red.500').value as string
          }
        },
        dark: {
          root: {
            background: '{surface.900}',
            color: 'currentColor',
            disabledBackground: '{surface.700}',
            invalidBorderColor: $dt('red.500').value as string
          }
        }
      }
    },

    panel: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}'
          }
        },
        dark: {
          root: {
            background: '{surface.900}'
          }
        }
      }
    },
    progressbar: {
      root: {
        height: '4px'
      },
      colorScheme: {
        light: {
          root: {
            background: '{surface.200}'
          }
        },
        dark: {
          root: {
            background: '{surface.900}'
          }
        }
      }
    },
    progressspinner: {
      root: {
        colorOne: $dt('red.500').value as string,
        colorTwo: $dt('red.500').value as string,
        colorThree: $dt('red.500').value as string,
        colorFour: $dt('red.500').value as string
      }
    },
    splitter: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.0}',
            color: 'currentColor'
          }
        },
        dark: {
          root: {
            background: '{surface.900}',
            color: 'currentColor'
          }
        }
      }
    },

    tabs: {
      tab: {
        color: 'currentColor'
      },
      tablist: {
        background: '{surface-b}'
      },
      tabpanel: {
        background: '{surface-b}',
        color: 'currentColor',
        padding: '0.5rem 1.5rem 1.5rem 1.5rem'
      }
    },
    tieredmenu: {
      colorScheme: {
        light: {
          root: {
            background: '{surface.100}',
            color: 'black'
          },
          item: {
            color: 'black'
          }
        },
        dark: {
          root: {
            background: '{surface.950}',
            color: 'white'
          },
          item: {
            color: 'white'
          }
        }
      }
    },
    tree: {
      colorScheme: {
        light: {
          node: {
            color: 'inherit',
            hoverBackground: '{surface.100}',
            selectedBackground: '{surface.200}'
          },
          nodeIcon: {
            color: 'inherit'
          }
        },
        dark: {
          node: {
            color: 'white',
            hoverBackground: '{surface.800}',
            selectedBackground: '{surface.900}'
          },
          nodeIcon: {
            color: 'inherit'
          }
        }
      }
    },
    csp: {
      nonce: '123456'
    }
  }
});
